/**
 * usage:
 *
 * import in component:
 * `import {useModal} from "./path/to/composables/useModal";`
 *
 * call when appropriate:
 * ```
 * const { showModal } = useModal();
 *
 * const elm = showModal({
 *   title: "Dynamic Modal",
 *   message: "<p>HTML Message to display</p>",
 *   buttonText: "Close",
 *   buttonClass: 'btn btn-primary',
 *   onShow: () => {
 *    // anything to do when the modal is displayed
 *   },
 *   onClose: () => {
 *     // any logic when the modal is closed (including if it was confirmed)
 *     console.info('modal closed')
 *   },
 *   onConfirm: () => {
 *    // any logic for if the button was clicked
 *    console.info("modal confirmed")
 *   }
 * });
 * ```
 *
 */
export function useModal() {
    function showModal(
        {
            icon = '',
            title = '',
            message = '',
            buttonText = 'Close',
            buttonClass = 'btn btn-grey',
            onShow = null,
            onClose = null,
            onConfirm = null
        }) {
        const uniqueId = `modal-${Date.now()}`;
        const modalHtml = `
      <div class="modal online-course-modal fade" id="${uniqueId}" tabindex="-1" aria-labelledby="${uniqueId}Label" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header border-0">
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body text-center">
            ${icon}
            ${title ? `<h3 class="modal-title fs-4" id="${uniqueId}Label">${title}</h3>` : ''}
              ${message}
            </div>
            <div class="modal-footer border-0">
              <button type="button" class="${buttonClass}" id="modalButton-${uniqueId}" data-bs-dismiss="modal">${buttonText}</button>
            </div>
          </div>
        </div>
      </div>
`;

        // Append modal to the DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Initialize Bootstrap modal
        const modalElm = document.getElementById(uniqueId);
        const modalInstance = new bootstrap.Modal(modalElm);

        if (typeof onShow === "function") {
            modalElm.addEventListener('show.bs.modal', () => {
                onShow()
            })
        }

        modalInstance.show();

        // Attach event listeners for modal events
        if (typeof onClose === "function") {
            modalElm.addEventListener('hidden.bs.modal', () => {
                onClose(); // Trigger callback when modal closes
                cleanup();
            });
        } else {
            // Cleanup even without an onClose handler
            modalElm.addEventListener('hidden.bs.modal', cleanup);
        }

        if (typeof onConfirm === "function") {
            document.getElementById("modalButton-" + uniqueId).addEventListener('click', () => {
                onConfirm()
            })
        }

        // Automatically clean up the modal from the DOM
        function cleanup() {
            modalInstance.dispose(); // Bootstrap cleanup
            document.body.removeChild(modalElm); // Remove modal from DOM
        }

        return modalElm;
    }

    return {showModal};
}